/**
 * App.jsx
 *
 * Ana bileşen — sadece state yönetimi ve layout sorumluluğu taşır.
 * SOLID - Single Responsibility: Hiçbir API kodu, render detayı veya
 *         stil hesabı içermez. Her şey ilgili bileşene delege edilir.
 */

import React, { useState, useEffect } from "react";
import SearchBar from "./components/SearchBar";
import ResultList from "./components/ResultList";
import StatusMessage from "./components/StatusMessage";
import RagAnswer from "./components/RagAnswer";
import AuthScreen from "./components/AuthScreen";
import { search, ragSearch } from "./api/searchClient";

function App() {
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "error" | "success"
  const [errorText, setErrorText] = useState("");
  const [searchData, setSearchData] = useState(null); // SearchResponse | RagSearchResponse
  const [isRagEnabled, setIsRagEnabled] = useState(true);
  const [username, setUsername] = useState(null);

  useEffect(() => {
    // Component mount olduğunda token varsa kullanıcıyı giriş yapmış say
    const token = localStorage.getItem("token");
    const storedUsername = localStorage.getItem("username");
    if (token && storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    setUsername(null);
    setSearchData(null);
    setStatus("idle");
  };

  async function handleSearch(query, topK) {
    setStatus("loading");
    setSearchData(null);
    setErrorText("");

    const fetchMethod = isRagEnabled ? ragSearch : search;
    const { data, error } = await fetchMethod(query, topK);

    if (error) {
      if (error.includes("401") || error.includes("Yetkisiz")) {
        // Token süresi dolmuş veya geçersiz
        handleLogout();
        return;
      }
      setStatus("error");
      setErrorText(error);
      return;
    }

    setSearchData(data);
    setStatus("success");
  }

  const isLoading = status === "loading";

  return (
    <div className="app">
      {/* Header */}
      <header className="app__header">
        <div className="app__header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="app__logo" aria-hidden="true">
              <span className="logo-dot logo-dot--pgvector" />
              <span className="logo-dot logo-dot--faiss" />
            </div>
            <div>
              <h1 className="app__title">VectorSearch UI</h1>
              <p className="app__subtitle">
                FAISS &amp; pgvector — Semantik Arama Karşılaştırması
              </p>
            </div>
          </div>
          
          {username && (
            <div className="header-auth">
              <span className="header-user">Merhaba, <strong>{username}</strong></span>
              <button onClick={handleLogout} className="header-logout-btn">
                Çıkış Yap
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Ana içerik */}
      <main className="app__main" id="main-content">
        {!username ? (
          <AuthScreen onAuthSuccess={setUsername} />
        ) : (
          <>
            <SearchBar onSearch={handleSearch} isLoading={isLoading} />

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  className="toggle-switch__input" 
                  checked={isRagEnabled}
                  onChange={(e) => setIsRagEnabled(e.target.checked)}
                  disabled={isLoading}
                />
                <span className="toggle-switch__slider"></span>
                <span className="toggle-switch__label">Yapay Zeka (RAG) Özeti</span>
              </label>
            </div>

            <div className={`app__results ${status === "success" && searchData && isRagEnabled ? "app__results--with-rag" : ""}`}>
              {/* Idle, loading ve error durumları */}
              {status !== "success" && (
                <StatusMessage status={status} errorText={errorText} />
              )}

              {/* Başarılı sonuçlar */}
              {status === "success" && searchData && (
                <>
                  <ResultList
                    results={searchData.results}
                    engine={searchData.engine}
                    latencyMs={searchData.latency_ms}
                    query={searchData.query}
                    translatedQuery={searchData.translated_query}
                  />
                  {isRagEnabled && (
                    <RagAnswer 
                      answer={searchData.rag_answer} 
                      isLoading={false} 
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="app__footer">
        <p>Çukurova Teknokent Staj Projesi · FAISS vs pgvector</p>
      </footer>
    </div>
  );
}

export default App;
